import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { defaultExperience, defaultSections, Experience } from "@reactive-resume/schema";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RichInput,
  Button,
} from "@reactive-resume/ui";
import { UseFormReturn, useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { useMemo, useState } from "react";
import { datesFromText } from "@reactive-resume/utils";
import * as Popover from "@radix-ui/react-popover";
import { Sparkles, X, Check } from "lucide-react";

import { AiAssist } from "@/client/components/ai-assist";
import { aiService } from "@/client/services/aiService";
import { toast } from "@/client/hooks/use-toast";
import { useResumeStore } from "@/client/stores/resume";

import { SectionDialog } from "../sections/shared/section-dialog";
import { URLInput } from "../sections/shared/url-input";
import { DatesDraft, ExperienceDatesField } from "../sections/shared/experience-dates-field";
import { experienceFormSchema as formSchema } from "./experience-form-schema";

type FormValues = z.input<typeof formSchema>;

export const ExperienceDialog = () => {
  const form = useForm<FormValues>({
    defaultValues: defaultExperience as FormValues,
    resolver: zodResolver(formSchema),
  });

  // Watch company and position fields to enable/disable generate button
  const company = useWatch({ control: form.control, name: "company" });
  const position = useWatch({ control: form.control, name: "position" });
  const date = useWatch({ control: form.control, name: "date" });
  // Older entries have only text: the pickers start from what it says.
  const textDates = useMemo(
    () => (datesFromText({ date }) ?? undefined) as DatesDraft | undefined,
    [date],
  );

  // Get job description and existing experiences for context
  const jobDescription = useResumeStore((state) => state.resume.jobDescription);
  const experienceData = useResumeStore(
    (state) => state.resume.data.sections.experience ?? defaultSections.experience,
  );

  // AI generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [appliedSuggestions, setAppliedSuggestions] = useState<Set<number>>(new Set());

  // Check if generate button should be enabled
  const canGenerate = Boolean(company && company.trim() && position && position.trim());

  const handleGenerateExperience = async () => {
    if (!canGenerate) return;

    setIsGenerating(true);
    try {
      // Gather existing experiences for context
      const existingExperiences = experienceData.items.slice(0, 3).map((exp) => ({
        company: exp.company,
        position: exp.position,
        summary: exp.summary,
      }));

      // Call AI service
      const result = await aiService.generateExperienceFromTitle(
        company,
        position,
        date || undefined,
        jobDescription?.trim() || undefined,
        existingExperiences.length > 0 ? existingExperiences : undefined,
      );

      if (result.suggestions && result.suggestions.length > 0) {
        setSuggestions(result.suggestions);
        setAppliedSuggestions(new Set()); // Reset applied tracking
        setShowSuggestions(true);
      } else {
        toast({
          variant: "error",
          title: t`Generation Failed`,
          description: t`No suggestions were generated. Please try again.`,
        });
      }
    } catch (error) {
      console.error("Error generating experience:", error);
      toast({
        variant: "error",
        title: t`Generation Failed`,
        description: (error as Error)?.message || t`Failed to generate experience bullet points.`,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApplySuggestion = (suggestion: string, index: number) => {
    const currentSummary = form.getValues("summary");

    // Append suggestion to existing content
    let newContent: string;
    if (!currentSummary || currentSummary === "<p></p>" || currentSummary.trim() === "") {
      // If empty, start fresh with the suggestion
      newContent = `<ul><li><p>${suggestion}</p></li></ul>`;
    } else {
      // If there's existing content, append the new bullet point
      // Check if the content already has a list structure
      if (currentSummary.includes("<ul>")) {
        // Insert before closing </ul>
        newContent = currentSummary.replace("</ul>", `<li><p>${suggestion}</p></li></ul>`);
      } else {
        // Wrap existing content and add new bullet
        newContent = `<ul><li><p>${currentSummary.replace(/<\/?p>/g, "")}</p></li><li><p>${suggestion}</p></li></ul>`;
      }
    }

    form.setValue("summary", newContent);

    // Mark as applied
    setAppliedSuggestions((prev) => new Set(prev).add(index));

    toast({
      title: t`Success`,
      description: t`Bullet point added to your experience summary`,
    });
  };

  return (
    // The form holds draft dates (a month or year can be empty while picking); the schema only lets
    // complete dates through to the saved item.
    <SectionDialog<Experience>
      id="experience"
      form={form as unknown as UseFormReturn<Experience>}
      defaultValues={defaultExperience}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          name="company"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Company`}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="position"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t({
                  message: "Position",
                  context: "Position held at a company, for example, Software Engineer",
                })}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="dates"
          control={form.control}
          render={({ field, fieldState }) => (
            <FormItem className="sm:col-span-2">
              <ExperienceDatesField
                value={(field.value as DatesDraft | undefined) ?? textDates}
                text={date}
                onChange={(dates, text) => {
                  field.onChange(dates);
                  form.setValue("date", text, { shouldDirty: true });
                }}
                error={fieldState.error?.message}
              />
            </FormItem>
          )}
        />

        <FormField
          name="location"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Location`}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="url"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Website`}</FormLabel>
              <FormControl>
                <URLInput {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="summary"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t`Summary`}</FormLabel>
              <FormControl>
                <RichInput
                  {...field}
                  content={field.value}
                  onChange={(value) => field.onChange(value)}
                  footer={(editor) => (
                    <div className="space-y-2">
                      <Popover.Root open={showSuggestions} onOpenChange={setShowSuggestions}>
                        <Popover.Anchor asChild>
                          <div className="rounded-lg bg-sky-50 dark:bg-sky-950/20 p-3 border border-sky-100 dark:border-sky-900/30">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={handleGenerateExperience}
                              disabled={!canGenerate || isGenerating}
                              className="w-full border-sky-200 bg-white hover:bg-sky-50 hover:border-sky-300 dark:border-sky-800 dark:bg-zinc-800 dark:hover:bg-sky-950/40 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isGenerating ? (
                                <>
                                  <Sparkles
                                    size={14}
                                    className="mr-1.5 flex-shrink-0 animate-pulse text-sky-600 dark:text-sky-400"
                                  />
                                  <span className="text-xs truncate">{t`Generating...`}</span>
                                </>
                              ) : (
                                <>
                                  <Sparkles
                                    size={14}
                                    className="mr-1.5 flex-shrink-0 text-sky-600 dark:text-sky-400"
                                  />
                                  <span className="text-xs truncate">{t`Generate with AI`}</span>
                                </>
                              )}
                            </Button>
                            {!canGenerate && (
                              <p className="mt-1.5 text-xs text-zinc-600 dark:text-zinc-400 text-center">
                                {t`Fill in company and position to generate bullet points`}
                              </p>
                            )}
                          </div>
                        </Popover.Anchor>
                        <Popover.Portal>
                          <Popover.Content
                            className="z-[60] w-[32rem] rounded-xl border-2 border-zinc-200 bg-white p-6 shadow-2xl dark:border-zinc-700 dark:bg-zinc-900"
                            sideOffset={8}
                            side="top"
                            align="center"
                          >
                            <div className="mb-4 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Sparkles size={18} className="text-sky-600 dark:text-sky-400" />
                                <h3 className="text-base font-semibold text-zinc-800 dark:text-zinc-200">
                                  {t`Experience Bullet Points`}
                                </h3>
                              </div>
                              <Popover.Close
                                className="inline-flex items-center justify-center rounded-md p-1 text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
                                aria-label="Close"
                              >
                                <X size={16} />
                              </Popover.Close>
                            </div>

                            <p className="mb-4 text-sm text-zinc-600 dark:text-zinc-400">
                              {t`Click "Apply" to add a bullet point to your experience. You can apply multiple suggestions.`}
                            </p>

                            <div className="max-h-[24rem] space-y-3 overflow-auto">
                              {suggestions.map((suggestion, index) => {
                                const isApplied = appliedSuggestions.has(index);
                                return (
                                  <div
                                    key={index}
                                    className="rounded-lg border border-zinc-200 bg-white p-4 transition-colors hover:border-sky-200 dark:border-zinc-700 dark:bg-zinc-800 dark:hover:border-sky-800"
                                  >
                                    <p className="mb-3 text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">
                                      {suggestion}
                                    </p>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant={isApplied ? "success" : "outline"}
                                      onClick={() => handleApplySuggestion(suggestion, index)}
                                      disabled={isApplied}
                                      className={
                                        isApplied
                                          ? "w-full bg-emerald-500/90 hover:bg-emerald-500/90 text-white border-emerald-600 cursor-default"
                                          : "w-full border-sky-200 hover:bg-sky-50 hover:border-sky-300 dark:border-sky-800 dark:hover:bg-sky-950/40"
                                      }
                                    >
                                      {isApplied ? (
                                        <>
                                          <Check size={14} className="mr-1.5" />
                                          <span className="text-xs">{t`Applied`}</span>
                                        </>
                                      ) : (
                                        <span className="text-xs">{t`Apply`}</span>
                                      )}
                                    </Button>
                                  </div>
                                );
                              })}
                            </div>

                            <Popover.Arrow
                              className="fill-white dark:fill-zinc-900"
                              width={20}
                              height={10}
                            />
                          </Popover.Content>
                        </Popover.Portal>
                      </Popover.Root>

                      <AiAssist editor={editor} />
                    </div>
                  )}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </SectionDialog>
  );
};
